import { useCallback, useMemo } from "react";

import { OnChangeFn, SortingState } from "@tanstack/react-table";

import {
  FieldDefinition,
  FieldsOption,
  FilterFormData,
} from "src/base-components/FilterPopover/types";
import { isFieldDefinition } from "src/base-components/FilterPopover/utils";
import { CASES_DEFAULT_SORT } from "src/cases/queries/queues";
import { useCasesPersistedParams } from "src/cases/queues/hooks/casesQueueParamsStore";
import {
  filterFormDataToUrlParams,
  parseSortParam,
  sortingStateToSortParam,
  urlParamsToFilterFormData,
} from "src/cases/queues/hooks/filterParamUtils";

const SORT_KEY = "sort";

const extractFilterParams = (
  params: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).filter(([key]) => key.startsWith("filter[")),
  );

type UseCasesQueueParamsResult = {
  params: Record<string, string>;
  filterParams: Record<string, string>;
  filterFormData: FilterFormData | undefined;
  hasActiveFilter: boolean;
  applyFilters: (formData: FilterFormData) => void;
  clearFilters: () => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
};

export const useCasesQueueParams = (
  queueId?: string,
  fields?: FieldsOption[],
): UseCasesQueueParamsResult => {
  const { params, actions } = useCasesPersistedParams({ key: queueId });

  const fieldDefs: FieldDefinition[] = useMemo(
    () => fields?.filter(isFieldDefinition) ?? [],
    [fields],
  );

  const filterFormData = useMemo(
    () =>
      fieldDefs.length > 0
        ? urlParamsToFilterFormData(params, fieldDefs)
        : undefined,
    [params, fieldDefs],
  );

  const filterParams = useMemo(() => extractFilterParams(params), [params]);

  const hasActiveFilter = useMemo(
    () => Object.keys(filterParams).length > 0,
    [filterParams],
  );

  const sorting = useMemo(
    () => [parseSortParam(params[SORT_KEY] ?? CASES_DEFAULT_SORT)],
    [params],
  );

  const applyFilters = useCallback(
    (formData: FilterFormData) => {
      const filterParams = filterFormDataToUrlParams(formData, fieldDefs);
      const sortParam = params[SORT_KEY];
      actions.setParams({
        ...filterParams,
        ...(sortParam ? { [SORT_KEY]: sortParam } : {}),
      });
    },
    [fieldDefs, params, actions],
  );

  const clearFilters = useCallback(() => {
    const sortParam = params[SORT_KEY];
    if (sortParam) {
      actions.setParams({ [SORT_KEY]: sortParam });
    } else {
      actions.clearParams();
    }
  }, [params, actions]);

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      const newSortParam = sortingStateToSortParam(newSorting);
      const filterParams = extractFilterParams(params);

      if (newSortParam) {
        actions.setParams({ ...filterParams, [SORT_KEY]: newSortParam });
      } else {
        actions.setParams(filterParams);
      }
    },
    [sorting, params, actions],
  );

  return {
    params,
    filterParams,
    filterFormData,
    hasActiveFilter,
    applyFilters,
    clearFilters,
    sorting,
    onSortingChange,
  };
};
